import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { IncidentType } from "@/gql/graphql";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { useUser } from "@/lib/Auth/useUser";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { reportIncidentMutation } from "@/app/Incidents/GraphQL/reportIncidentMutation";
import { ReportIncidentForm } from "@/app/Incidents/Forms/ReportIncidentForm";
import {
  getReportIncidentSchema,
  ReportIncidentFormValues,
} from "@/app/Incidents/Forms/reportIncidentSchema";
import dayjs from "dayjs";
import {
  DATE_TIME_FORMAT,
  toDateTimeTzString,
} from "@/lib/Formatters/toDateTimeString";

export function ReportIncidentDialog({
  workOrderId,
  assignmentId,
}: {
  workOrderId?: string;
  assignmentId?: string;
}) {
  const t = useTranslations("incident");
  const { isAdmin } = useTenant();
  const user = useUser();
  const navigate = useNavigate();
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(reportIncidentMutation);

  const schema = getReportIncidentSchema(isAdmin, t);
  const canPickAssignment = !assignmentId;

  return (
    <BaseDialog
      title={t("report.title")}
      subtitle={t("report.subtitle")}
      size="4xl"
    >
      <GenericForm<ReportIncidentFormValues>
        initialValues={{
          work_order_id: workOrderId ?? "",
          work_order_assignment_id: assignmentId ?? "",
          found_at: dayjs().format(DATE_TIME_FORMAT),
          type: IncidentType.ExteriorDamage,
          description: "",
          follow_up_contact: isAdmin ? "" : user.email,
          open_claim: false,
          insurer: "",
          claim_reference: "",
          excess_amount: null,
          photos: [],
          claim_documents: [],
        }}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={async (values) => {
          const { reportIncident } = await mutateAsync({
            input: {
              work_order_assignment_id: values.work_order_assignment_id,
              found_at: toDateTimeTzString(values.found_at),
              type: values.type,
              description: values.description,
              follow_up_contact: values.follow_up_contact,
              photo_file_paths: values.photos.map((upload) => upload.filePath),
              open_claim: values.open_claim,
              excess_amount: values.excess_amount,
              insurer: values.insurer || null,
              claim_reference: values.claim_reference || null,
              claim_document_file_paths: values.claim_documents.map(
                (upload) => upload.filePath,
              ),
            },
          });

          toast.success(
            t("report.success", { reference: reportIncident.reference }),
          );

          await navigate({
            from: "/$tenant/$orgId/supplier/{-$supplierId}",
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/incidents/$incidentId",
            params: (prev) => ({
              ...prev,
              incidentId: reportIncident.id,
            }),
          });

          await close();
        }}
      >
        <ReportIncidentForm canPickAssignment={canPickAssignment} />
        <DialogSubmitButton
          text={t("report.submit")}
          Icon={ExclamationTriangleIcon}
        />
      </GenericForm>
    </BaseDialog>
  );
}
